import React from "react";
import { createPlanMixUpdates } from "components/common/plan/EditableTableActions";
import { savedMixToLayerState } from "components/common/plan/layerEditor/conversions";
import { MixLayerState } from "components/common/plan/layerEditor/types";
import {
  applyMixUpdateToRows,
  detectManuallyLayeredMixes,
  mergeRowsAndMix,
} from "components/common/plan/utils";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import { PlanId } from "components/common/boundary/PlanId";
import {
  ChildMixUpdate,
  generatedApi,
  LayerEditMode,
  ProductionMixResultRead,
  useSummarisePlanResultsMutation,
  useUpdatePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  makePlanPeriodId,
  parsePlanPeriodId,
  planMixTableEditsSlice,
  PlanPeriodId,
  selectPlanMixTableEdits,
} from "src/store/slices/planMixesTableEditSlice";
import { useAppDispatch, useAppStore } from "src/store/store";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import {
  makeHandleOnSaveChanges,
  resolveSavedMixUpdate,
} from "./savePlanMixes";

const CELL_COMMIT_DELAY_MS = 500;

interface UseLayerEditorOptions {
  focussedPlanId: PlanId;
  focussedPeriod: Period;
  formPlanId: PlanId;
  results: Record<PlanPeriodId, ProductionMixResultRead[]>;
  tenantName: string;
  searchId: number | null;
  setPlanId: (planId: PlanId) => void;
  comment: string;
  setComment: (comment: string) => void;
  layerEditMode: LayerEditMode;
}

interface UseLayerEditorResult {
  showingLayers: boolean;
  layerPreviewData: MixLayerState[] | null;
  hasLayerEdits: boolean;
  mixIdsWithEdits: ReadonlySet<number>;
  handleShowLayers: () => void;
  handleShowWeights: () => void;
  handleSave: () => Promise<void>;
  handleDiscard: () => void;
  handleLayerStateChange: (
    updates: ChildMixUpdate[],
    editedMixIds: ReadonlySet<number>
  ) => void;
  handleMixLayerStateChange: (
    mixId: number,
    mixUpdates: ChildMixUpdate[],
    hasMixEdits: boolean
  ) => void;
  getLayerStateForMix: (mixId: number) => MixLayerState | null;
  resetMixLayers: (mixId: number) => void;
}

export const useLayerEditor = ({
  focussedPlanId,
  focussedPeriod,
  formPlanId,
  results,
  tenantName,
  searchId,
  setPlanId,
  comment,
  setComment,
  layerEditMode,
}: UseLayerEditorOptions): UseLayerEditorResult => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [doUpdatePlanResultsMutation] = useUpdatePlanResultsMutation();
  const [getResult] = generatedApi.useLazyGetResultQuery();
  const notifyStatus = useNotifyStatus();
  const { t } = useTenantTranslation();

  const [editorPhase, setEditorPhase] = React.useState<"weights" | "layers">(
    "weights"
  );
  const pendingLayerUpdatesRef = React.useRef<ChildMixUpdate[]>([]);
  // Inferred from the stored layout diverging from the tenant's auto
  // stratification, so the mix must round-trip as manual. This is never a user
  // action and must not count towards the unsaved-edits (dirty) gate.
  const [mixIdsInferredManual, setMixIdsInferredManual] = React.useState<
    Set<number>
  >(() => new Set());
  // Mixes the user has actively re-layered this session — the dirty signal.
  const [mixIdsUserEdited, setMixIdsUserEdited] = React.useState<Set<number>>(
    () => new Set()
  );
  const mixIdsWithEdits = React.useMemo(
    () => new Set<number>([...mixIdsInferredManual, ...mixIdsUserEdited]),
    [mixIdsInferredManual, mixIdsUserEdited]
  );
  // One-shot: mixes that just got reset await the next mount-emit so we can
  // copy the server's re-stratified layers back into the rows. Without this,
  // row.layers retains the user's manual split and hasWeightEdits stays true
  // even though the visible layers match the server's auto-layering.
  const mixIdsAwaitingRowSyncRef = React.useRef<Set<number>>(new Set());
  // One-shot per plan/period: mark which periods we've already inferred
  // manually-layered status for, so that a user-driven reset isn't undone by
  // the next summarise response re-detecting the same mix from the DB.
  const detectedManualPeriodsRef = React.useRef<Set<PlanPeriodId>>(new Set());

  const focussedPlanPeriodId = makePlanPeriodId(focussedPlanId, focussedPeriod);

  const [, summariseState] = useSummarisePlanResultsMutation({
    fixedCacheKey: focussedPlanPeriodId,
  });
  const summariseData = summariseState.data;

  const layerPreviewData = React.useMemo<MixLayerState[] | null>(() => {
    const periodResults = results[focussedPlanPeriodId] ?? [];
    if (periodResults.length === 0) return null;
    const summarisedById = new Map(
      (summariseData?.mixes ?? []).map(
        (summarisedMix) =>
          [summarisedMix.original_mix_id, summarisedMix] as const
      )
    );
    return periodResults.map((result) => {
      // An inferred-manual mix the user hasn't touched must show its stored
      // layout; the per_basket_totals summarise re-stratifies it, and that
      // re-layering is exactly what we discard here.
      if (
        mixIdsInferredManual.has(result.mix.id) &&
        !mixIdsUserEdited.has(result.mix.id)
      ) {
        return savedMixToLayerState(result);
      }
      const summarised = summarisedById.get(result.mix.id);
      if (summarised) {
        return savedMixToLayerState({
          ...result,
          mix: {
            ...result.mix,
            baskets: summarised.mix.baskets,
            summary: summarised.mix.summary,
          },
        });
      }
      // Fall back to deriving from the current rows so the popover can render
      // before the first summarise response lands.
      const planEdits = selectPlanMixTableEdits(
        store.getState(),
        focussedPlanId,
        focussedPeriod
      );
      const rows = planEdits?.editedRows ?? [];
      return savedMixToLayerState(mergeRowsAndMix(rows, result));
    });
  }, [
    results,
    focussedPlanPeriodId,
    summariseData,
    store,
    focussedPlanId,
    focussedPeriod,
    mixIdsInferredManual,
    mixIdsUserEdited,
  ]);

  const hasLayerEdits = mixIdsUserEdited.size > 0;

  // First summarise after a plan loads (or after save, which mints a new
  // planPeriodId) sends per_basket_totals for every mix because the set is
  // empty, so the response baskets are the tenant's auto-stratification. Any
  // mix whose stored layout diverges from auto must have been saved manually,
  // so it goes into mixIdsInferredManual to round-trip verbatim on later
  // summarises — deliberately NOT mixIdsUserEdited, so a just-loaded plan is
  // never treated as dirty. React's "calculate state during render" pattern
  // lets us derive the update from summariseData without a useEffect; the
  // period ref guarantees one-shot per planPeriodId so a user's explicit reset
  // isn't undone next render.
  const [seenSummariseDataForDetection, setSeenSummariseDataForDetection] =
    React.useState(summariseData);
  if (seenSummariseDataForDetection !== summariseData) {
    setSeenSummariseDataForDetection(summariseData);
    if (
      layerEditMode !== "none" &&
      !detectedManualPeriodsRef.current.has(focussedPlanPeriodId) &&
      summariseData?.mixes
    ) {
      const periodResults = results[focussedPlanPeriodId] ?? [];
      if (periodResults.length > 0) {
        const detected = detectManuallyLayeredMixes(
          periodResults,
          summariseData.mixes,
          mixIdsWithEdits
        );
        detectedManualPeriodsRef.current.add(focussedPlanPeriodId);
        if (detected.size > 0) {
          setMixIdsInferredManual((previous) => {
            let merged: Set<number> | null = null;
            for (const mixId of detected) {
              if (previous.has(mixId)) continue;
              merged ??= new Set(previous);
              merged.add(mixId);
            }
            return merged ?? previous;
          });
        }
      }
    }
  }

  const handleShowLayers = React.useCallback(() => {
    setEditorPhase("layers");
  }, []);

  const handleShowWeights = React.useCallback(() => {
    setEditorPhase("weights");
  }, []);

  const propagateUpdatesToRows = React.useCallback(
    (updates: ChildMixUpdate[]) => {
      const planEdits = selectPlanMixTableEdits(
        store.getState(),
        focussedPlanId,
        focussedPeriod
      );
      if (!planEdits) return;
      let nextRows = planEdits.editedRows;
      for (const update of updates) {
        for (const mixId of update.parent_mix_ids) {
          nextRows = applyMixUpdateToRows(nextRows, mixId, update);
        }
      }
      if (nextRows !== planEdits.editedRows) {
        dispatch(
          planMixTableEditsSlice.actions.update([
            focussedPlanId,
            focussedPeriod,
            nextRows,
          ])
        );
      }
    },
    [store, focussedPlanId, focussedPeriod, dispatch]
  );

  const drainAwaitingRowSync = React.useCallback(
    (updates: ChildMixUpdate[]) => {
      if (mixIdsAwaitingRowSyncRef.current.size === 0) return;
      const planEdits = selectPlanMixTableEdits(
        store.getState(),
        focussedPlanId,
        focussedPeriod
      );
      if (!planEdits) return;
      let nextRows = planEdits.editedRows;
      const synced = new Set<number>();
      for (const update of updates) {
        for (const mixId of update.parent_mix_ids) {
          if (!mixIdsAwaitingRowSyncRef.current.has(mixId)) continue;
          const applied = applyMixUpdateToRows(nextRows, mixId, update);
          // Clear the pending flag only once the awaited re-stratified layers
          // actually change the rows. A reset fires several emits before the
          // backend's auto-layering lands; a stale, no-op emit must not consume
          // the flag, or those auto layers would never reach the rows and the
          // reset would leave nothing to save or discard.
          if (applied !== nextRows) {
            nextRows = applied;
            synced.add(mixId);
          }
        }
      }
      if (nextRows !== planEdits.editedRows) {
        dispatch(
          planMixTableEditsSlice.actions.update([
            focussedPlanId,
            focussedPeriod,
            nextRows,
          ])
        );
      }
      for (const mixId of synced) {
        mixIdsAwaitingRowSyncRef.current.delete(mixId);
      }
    },
    [store, focussedPlanId, focussedPeriod, dispatch]
  );

  const handleLayerStateChange = React.useCallback(
    (updates: ChildMixUpdate[], editedMixIds: ReadonlySet<number>) => {
      pendingLayerUpdatesRef.current = updates;
      drainAwaitingRowSync(updates);
      // editedMixIds is exactly the set of mixes whose state diverges from
      // LayerEditor's initialState. Once a manual edit round-trips via
      // summarise (layer_source=manual passes through), the next mount-emit
      // arrives with an empty set even though the mix was just user-edited.
      // Treat mixIdsUserEdited as sticky: only ADD here; cleared on save or
      // discard.
      if (editedMixIds.size === 0) return;
      setMixIdsUserEdited((previous) => {
        let merged: Set<number> | null = null;
        for (const mixId of editedMixIds) {
          if (previous.has(mixId)) continue;
          merged ??= new Set(previous);
          merged.add(mixId);
        }
        return merged ?? previous;
      });
      const editedUpdates = updates.filter((update) =>
        update.parent_mix_ids.some((mixId) => editedMixIds.has(mixId))
      );
      propagateUpdatesToRows(editedUpdates);
    },
    [propagateUpdatesToRows, drainAwaitingRowSync]
  );

  const handleMixLayerStateChange = React.useCallback(
    (mixId: number, mixUpdates: ChildMixUpdate[], hasMixEdits: boolean) => {
      const otherMixUpdates = pendingLayerUpdatesRef.current.filter(
        (update) => !update.parent_mix_ids.includes(mixId)
      );
      pendingLayerUpdatesRef.current = [...otherMixUpdates, ...mixUpdates];
      drainAwaitingRowSync(mixUpdates);
      // Same sticky-add semantics as handleLayerStateChange.
      if (!hasMixEdits) return;
      setMixIdsUserEdited((previous) => {
        if (previous.has(mixId)) return previous;
        const next = new Set(previous);
        next.add(mixId);
        return next;
      });
      propagateUpdatesToRows(mixUpdates);
    },
    [propagateUpdatesToRows, drainAwaitingRowSync]
  );

  const resetMixLayers = React.useCallback((mixId: number) => {
    pendingLayerUpdatesRef.current = pendingLayerUpdatesRef.current.filter(
      (update) => !update.parent_mix_ids.includes(mixId)
    );
    const withoutMix = (previous: Set<number>): Set<number> => {
      if (!previous.has(mixId)) return previous;
      const next = new Set(previous);
      next.delete(mixId);
      return next;
    };
    setMixIdsUserEdited(withoutMix);
    setMixIdsInferredManual(withoutMix);
    mixIdsAwaitingRowSyncRef.current.add(mixId);
  }, []);

  const getLayerStateForMix = React.useCallback(
    (mixId: number): MixLayerState | null =>
      layerPreviewData?.find((mix) => mix.mixId === mixId) ?? null,
    [layerPreviewData]
  );

  const buildEditedMixesForSave = React.useCallback((): Record<
    PlanPeriodId,
    ChildMixUpdate[]
  > => {
    const focusedPendingByMixId = new Map(
      pendingLayerUpdatesRef.current.map((update) => {
        const targetMixId = update.parent_mix_ids[0];
        return [targetMixId, update] as const;
      })
    );
    return typeSafeObjectFromEntries(
      typeSafeObjectEntries(results).map(([planPeriodId, periodResults]) => {
        const [planId, period] = parsePlanPeriodId(planPeriodId);
        const planEdits = selectPlanMixTableEdits(
          store.getState(),
          planId,
          period
        );
        if (!planEdits) return [planPeriodId, []];
        const updates = createPlanMixUpdates(
          periodResults,
          periodResults.map((result) =>
            mergeRowsAndMix(planEdits.editedRows, result)
          )
        ).map((update): ChildMixUpdate => {
          const isFocusedPeriod = planPeriodId === focussedPlanPeriodId;
          const mixId = update.parent_mix_ids[0];
          const isManual =
            isFocusedPeriod &&
            mixId !== undefined &&
            mixIdsWithEdits.has(mixId);
          // Use the layer-editor payload only for a mix the user re-layered in
          // the editor. For a mix that is manual only by inference, that
          // payload carries its stored layout, not the weights-table edit.
          const layerEditorUpdate =
            isManual && mixIdsUserEdited.has(mixId)
              ? focusedPendingByMixId.get(mixId)
              : undefined;
          return resolveSavedMixUpdate({
            tableUpdate: update,
            layerEditorUpdate,
            isManual,
          });
        });
        return [planPeriodId, updates];
      })
    );
  }, [results, store, focussedPlanPeriodId, mixIdsWithEdits, mixIdsUserEdited]);

  const handleSave = React.useCallback(async () => {
    typeSafeObjectKeys(results).forEach((planPeriodId) => {
      const [planId, period] = parsePlanPeriodId(planPeriodId);
      dispatch(
        planMixTableEditsSlice.actions.setIsSubmitting([planId, period])
      );
    });
    try {
      await new Promise((resolve) => setTimeout(resolve, CELL_COMMIT_DELAY_MS));
      const handleOnSaveChanges = makeHandleOnSaveChanges(
        searchId,
        formPlanId,
        doUpdatePlanResultsMutation,
        tenantName,
        setPlanId,
        getResult,
        dispatch,
        comment,
        "per_basket_totals"
      );
      const editedMixes = buildEditedMixesForSave();
      if (Object.values(editedMixes).every((updates) => updates.length === 0)) {
        return;
      }
      const updatedPlan = await handleOnSaveChanges(editedMixes);
      if (!updatedPlan) {
        notifyStatus({ type: "error", text: t("layerSaveFailed") });
        return;
      }
      pendingLayerUpdatesRef.current = [];
      mixIdsAwaitingRowSyncRef.current = new Set();
      setMixIdsUserEdited(new Set());
      setMixIdsInferredManual(new Set());
      setComment("");
      typeSafeObjectKeys(results).forEach((planPeriodId) => {
        const [planId, period] = parsePlanPeriodId(planPeriodId);
        dispatch(planMixTableEditsSlice.actions.clear([planId, period]));
      });
    } finally {
      typeSafeObjectKeys(results).forEach((planPeriodId) => {
        const [planId, period] = parsePlanPeriodId(planPeriodId);
        dispatch(
          planMixTableEditsSlice.actions.setClearSubmitting([planId, period])
        );
      });
    }
  }, [
    dispatch,
    results,
    searchId,
    formPlanId,
    doUpdatePlanResultsMutation,
    tenantName,
    setPlanId,
    getResult,
    comment,
    setComment,
    buildEditedMixesForSave,
    notifyStatus,
    t,
  ]);

  const handleDiscard = React.useCallback(() => {
    typeSafeObjectKeys(results).forEach((planPeriodId) => {
      const [planId, period] = parsePlanPeriodId(planPeriodId);
      dispatch(planMixTableEditsSlice.actions.clear([planId, period]));
      detectedManualPeriodsRef.current.delete(planPeriodId);
    });
    pendingLayerUpdatesRef.current = [];
    mixIdsAwaitingRowSyncRef.current = new Set();
    setMixIdsUserEdited(new Set());
    setMixIdsInferredManual(new Set());
    setEditorPhase("weights");
  }, [dispatch, results]);

  const showingLayers = editorPhase === "layers";

  return {
    showingLayers,
    layerPreviewData,
    hasLayerEdits,
    mixIdsWithEdits,
    handleShowLayers,
    handleShowWeights,
    handleSave,
    handleDiscard,
    handleLayerStateChange,
    handleMixLayerStateChange,
    getLayerStateForMix,
    resetMixLayers,
  };
};
